import { validateCreationFields } from "../../../_shared/_logic/work-order-creation"
import type { AddressCandidate } from "../../../_shared/_logic/batch-upload-contract"
import type { PlanningUnitContactContext } from "../../../../../../../../../../../../../api/kehrblatt/planning"
import { normalizeCountryLabel } from "../../../../../../../../../../../../../_shared/_logic/countries"
import { formatPropertyCustomerRole } from "../../../../../../../../../../../_shared/_logic/customer-roles"
import { planningUnitContactContextKey } from "../../../../../../../../../../../_shared/_logic/planning-contact-contexts"
import { isBuildingPropertyUnit } from "../../../../../../../../../../../_shared/_logic/units"
import { orderBatchPropertyUnits } from "../_shared/_logic/unit-selection"
import {
  openWorkOrderWarningForSelection,
  type OpenWorkOrderWarningMap,
} from "./openWorkOrderWarnings"
import type { LoadListRowDraft } from "./loadListState.svelte"
import { BATCH_PLANNING_MAX_ROWS } from "./planning-payloads"

import type {
  UnitOptionPresentation,
  LoadListTableRow,
} from "../_shared/_logic/load-list-row-model"

const text = (value: unknown) => String(value ?? "").trim()
/** Reuse the backend's scope/role/primary ranking for every display; never re-sort by name here. */
export const buildUnitOptionPresentation = ({
  unit,
  context,
  disabledReason = null,
}: {
  unit: PropertyUnit
  context: PlanningUnitContactContext | undefined
  disabledReason?: string | null
}): UnitOptionPresentation => {
  const type = isBuildingPropertyUnit(unit) ? "Gebäudeeinheit" : "Wohneinheit"
  const number = text(unit.unit_number)
  const headline = number ? `${type} (${number})` : type
  const primary = context?.primary_contact
  const contacts = [...(context?.contacts ?? [])]

  if (
    primary &&
    !contacts.some(
      (contact) =>
        contact.customer_id === primary.customer_id &&
        contact.role === primary.role &&
        contact.assignment_scope === primary.assignment_scope,
    )
  )
    contacts.push(primary)

  const subline = !context
    ? "Ansprechpartner werden geladen…"
    : contacts.length
      ? contacts
          .map((contact) => `${formatPropertyCustomerRole(contact.role)}: ${contact.display_name}`)
          .join("; ")
      : "Kein Ansprechpartner hinterlegt"

  return {
    value: String(unit.id),
    label: `${headline} · ${subline}`,
    headline,
    subline,
    selectable: !disabledReason,
    disabledReason,
  }
}

/** Project immutable table values without putting API objects or mutable drafts inside cells. */
export const projectLoadListRows = ({
  rows,
  candidates,
  properties,
  contactContexts,
  warningsByTarget = {},
  loadedWarningPropertyIds = [],
  loadingWarningPropertyIds = [],
  failedWarningPropertyIds = [],
  contextLoadFailed = false,
}: {
  rows: LoadListRowDraft[]
  candidates: AddressCandidate[]
  properties: Property[]
  contactContexts: PlanningUnitContactContext[]
  warningsByTarget?: OpenWorkOrderWarningMap
  loadedWarningPropertyIds?: string[]
  loadingWarningPropertyIds?: string[]
  failedWarningPropertyIds?: string[]
  contextLoadFailed?: boolean
}): LoadListTableRow[] => {
  const propertiesById = new Map(properties.map((property) => [property.id, property]))
  const candidatesByKey = new Map(candidates.map((candidate) => [candidate.key, candidate]))
  const contextsByTarget = new Map(
    contactContexts.map((context) => [
      planningUnitContactContextKey(context.property_id, context.property_unit_id),
      context,
    ]),
  )
  const contactsByProperty = new Map<string, string[]>()
  for (const context of contactContexts) {
    const labels = contactsByProperty.get(context.property_id) ?? []
    labels.push(
      ...[...context.contacts, ...(context.primary_contact ? [context.primary_contact] : [])].map(
        (contact) => `${formatPropertyCustomerRole(contact.role)} ${contact.display_name}`,
      ),
    )
    contactsByProperty.set(context.property_id, labels)
  }
  const occupied = new Map<string, { id: string; number: number }>()
  rows.forEach((row, index) => {
    if (!row.removed && row.propertyId && row.selectedUnitId) {
      occupied.set(`${row.propertyId}:${row.selectedUnitId}`, { id: row.id, number: index + 1 })
    }
  })
  const loadedWarnings = new Set(loadedWarningPropertyIds)
  const loadingWarnings = new Set(loadingWarningPropertyIds)
  const failedWarnings = new Set(failedWarningPropertyIds)
  const activeCount = rows.filter((row) => !row.removed).length
  const optionsByProperty = new Map<string, UnitOptionPresentation[]>()
  const rowPropertyIds = new Set(rows.map((row) => row.propertyId))
  for (const property of properties) {
    if (!rowPropertyIds.has(property.id)) continue

    optionsByProperty.set(
      property.id,
      orderBatchPropertyUnits(property.units).map((unit) => {
        const key = planningUnitContactContextKey(property.id, String(unit.id))
        const taken = occupied.get(`${property.id}:${unit.id}`)
        const presentation = buildUnitOptionPresentation({
          unit,
          context: contextsByTarget.get(key),
          disabledReason: taken ? `Bereits in Zeile ${taken.number} ausgewählt.` : null,
        })

        if (contextLoadFailed && !contextsByTarget.has(key)) {
          presentation.subline = "Ansprechpartner konnten nicht geladen werden"
          presentation.label = `${presentation.headline} · ${presentation.subline}`
        }

        return presentation
      }),
    )
  }

  return rows.map((row, index) => {
    const candidate = candidatesByKey.get(row.sourceCandidateKey)
    const property = propertiesById.get(row.propertyId ?? "")
    const unresolved = !property ? candidate?.backlogResolution : null
    const payload = property ?? candidate?.payload
    const street = [payload?.street, payload?.house_number].filter(Boolean).join(" ").trim()
    const locality = [payload?.zip, payload?.city].filter(Boolean).join(" ")
    const address =
      street ||
      unresolved?.address ||
      (unresolved
        ? `Kundennummer ${unresolved.customerNumber}`
        : locality || "Unbekannte Liegenschaft")
    const options = optionsByProperty.get(row.propertyId ?? "") ?? []
    const selectedUnitId = options.some((option) => option.value === row.selectedUnitId)
      ? row.selectedUnitId
      : null
    const unitOptions = options.map((option) =>
      option.value === selectedUnitId
        ? { ...option, selectable: true, disabledReason: null }
        : option,
    )
    const needsUnitSelection = Boolean(property && !selectedUnitId)
    const copyDisabledReason = row.removed
      ? "Entfernte Zeilen können nicht kopiert werden."
      : !property
        ? "Bitte zuerst die Liegenschaft zuordnen."
        : !selectedUnitId
          ? "Bitte zuerst eine Einheit auswählen."
          : options.length <= 1
            ? "Diese Liegenschaft besitzt keine weitere Einheit."
            : !unitOptions.some((option) => option.value !== selectedUnitId && option.selectable)
              ? "Alle Einheiten dieser Liegenschaft sind bereits ausgewählt."
              : activeCount >= BATCH_PLANNING_MAX_ROWS
                ? "Pro Durchgang sind maximal 10.000 Planungszeilen möglich."
                : null

    return {
      id: row.id,
      key: row.sourceCandidateKey,
      rowNumber: index + 1,
      propertyId: row.propertyId,
      address,
      addressSearchText: [
        address,
        locality,
        normalizeCountryLabel(payload?.country),
        payload?.address_extra,
      ]
        .filter(Boolean)
        .join(" "),
      contactSearchText: (contactsByProperty.get(row.propertyId ?? "") ?? []).join(" "),
      removed: row.removed,
      status: property ? "existing" : "pending",
      selectedUnitId,
      unitCount: options.length,
      unitOptions,
      needsUnitSelection,
      needsAttention: !property || needsUnitSelection,
      configurationError: validateCreationFields(row.orderFields),
      attentionReason: !property
        ? unresolved
          ? "backlog_unresolved"
          : "address_missing"
        : needsUnitSelection
          ? "unit_missing"
          : null,
      backlogResolution: unresolved,
      copyOriginRowId: row.copyOriginRowId,
      copyDisabledReason,
      workWarnings: row.workWarnings ?? [],
      workEvidence: row.workEvidence ?? [],
      workTypesFromImport: Boolean(row.workTypesFromImport),
      openWorkOrderWarning: loadedWarnings.has(row.propertyId ?? "")
        ? openWorkOrderWarningForSelection({
            propertyId: row.propertyId,
            selectedUnitIds: selectedUnitId ? [selectedUnitId] : [],
            warningsByTarget,
          })
        : null,
      openWorkOrderWarningLoading: Boolean(property && !loadedWarnings.has(property.id)),
      openWorkOrderWarningState: failedWarnings.has(row.propertyId ?? "")
        ? "failed"
        : loadedWarnings.has(row.propertyId ?? "")
          ? "checked"
          : loadingWarnings.has(row.propertyId ?? "")
            ? "loading"
            : "pending",
    }
  })
}

export const loadListRowReferences = (rows: LoadListTableRow[]): string => {
  const references = rows.slice(0, 10).map((row) => `Zeile ${row.rowNumber} – ${row.address}`)

  if (rows.length > 10) references.push(`und ${rows.length - 10} weitere Zeilen`)

  return references.join("; ")
}
